/**
 * Comprehensive Pronunciation Results Component
 * Displays detailed pronunciation assessment with prosody support
 */

import React from 'react';
import { SessionPronunciationAnalytics } from './types';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { InfoIcon } from '../SvgConsolidated/SvgIcons';

interface PronunciationResultsProps {
  data: SessionPronunciationAnalytics;
  sessionId: string;
}

export default function PronunciationResults({ data, sessionId }: PronunciationResultsProps) {
  // React hooks MUST be called before any early returns
  const [errorFilter, setErrorFilter] = React.useState<string>('all');
  const [sortBy, setSortBy] = React.useState<'accuracy' | 'word'>('accuracy');

  if (!data.has_assessment) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
          <InfoIcon className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          No Pronunciation Assessment
        </h3>
        <p className="text-gray-600">
          {data.message || 'This session does not have pronunciation assessment data.'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Students need to enable "Pronunciation Feedback" when reading stories.
        </p>
      </div>
    );
  }

  const { assessment, word_details, statistics } = data;

  if (!assessment) {
    return null;
  }

  // Prepare scores data for chart
  const scoresData = [
    { name: 'Accuracy', score: assessment.scores.accuracy, color: '#3b82f6' },
    { name: 'Fluency', score: assessment.scores.fluency, color: '#10b981' },
    { name: 'Completeness', score: assessment.scores.completeness, color: '#f59e0b' },
    { name: 'Prosody', score: assessment.scores.prosody, color: '#8b5cf6' },
  ];

  // Overall score color
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 75) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  // Accuracy distribution for pie chart
  const accuracyDistributionData = statistics ? [
    { name: 'Excellent (90-100)', value: statistics.accuracy_distribution.excellent, color: '#10b981' },
    { name: 'Good (75-89)', value: statistics.accuracy_distribution.good, color: '#3b82f6' },
    { name: 'Fair (60-74)', value: statistics.accuracy_distribution.fair, color: '#f59e0b' },
    { name: 'Needs Work (0-59)', value: statistics.accuracy_distribution.needs_work, color: '#ef4444' },
  ].filter(item => item.value > 0) : [];

  // Error distribution data
  const errorDistributionData = statistics && statistics.error_distribution
    ? Object.entries(statistics.error_distribution).map(([error, count]) => ({
        error: error === 'None' ? 'Correct' : error,
        count: count
      }))
    : [];

  const filteredWords = word_details ? word_details.filter(word => {
    if (errorFilter === 'all') return true;
    if (errorFilter === 'errors') return word.error_type !== 'None';
    return word.error_type === errorFilter;
  }).sort((a, b) => {
    if (sortBy === 'accuracy') return a.accuracy_score - b.accuracy_score;
    return a.word.localeCompare(b.word);
  }) : [];

  const uniqueErrorTypes = word_details
    ? Array.from(new Set(word_details.map(w => w.error_type)))
    : [];

  return (
    <div className="space-y-6">
      {/* Overall Score Card */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Overall Pronunciation Score
            </h3>
            <p className="text-sm text-gray-600">
              {assessment.assessment_mode === 'scripted' ? '📖 Scripted' : '🗣️ Unscripted'} Assessment • 
              {' '}{assessment.locale} • 
              {' '}{Math.round(assessment.duration_seconds)}s duration
            </p>
          </div>
          <div className={`text-5xl font-bold px-6 py-3 rounded-lg border-2 ${getScoreColor(assessment.scores.overall_pronunciation)}`}>
            {Math.round(assessment.scores.overall_pronunciation)}
          </div>
        </div>
      </div>

      {/* Score Breakdown */}
      <div className="grid md:grid-cols-4 gap-4">
        {scoresData.map((item) => (
          <div key={item.name} className="bg-white rounded-lg p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{item.name}</span>
              <span 
                className="text-2xl font-bold" 
                style={{ color: item.color }}
              >
                {Math.round(item.score)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${item.score}%`,
                  backgroundColor: item.color
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Scores Bar Chart */}
      <div className="bg-white rounded-lg p-6 border shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Score Breakdown</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={scoresData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Bar dataKey="score" fill="#8884d8" label={{ position: 'top' }}>
              {scoresData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Text Comparison */}
      {assessment.reference_text && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-6 border shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span>📖</span> Reference Text
            </h3>
            <div className="text-gray-700 leading-relaxed p-4 bg-gray-50 rounded border">
              {assessment.reference_text}
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span>🎤</span> Recognized Speech
            </h3>
            <div className="text-gray-700 leading-relaxed p-4 bg-blue-50 rounded border border-blue-200">
              {assessment.recognized_text}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Accuracy Distribution Pie Chart */}
          {accuracyDistributionData.length > 0 && (
            <div className="bg-white rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Word Accuracy Distribution
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={accuracyDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => {
                      const name = entry.name || '';
                      const value = entry.value || 0;
                      const percent = typeof entry.percent === 'number' ? entry.percent : 0;
                      return `${name.split(' ')[0]}: ${value} (${(percent * 100).toFixed(0)}%)`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {accuracyDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 text-center text-sm text-gray-600">
                Total Words: {statistics.word_count} • Avg Accuracy: {Math.round(statistics.avg_word_accuracy)}%
              </div>
            </div>
          )}

          {/* Error Distribution */}
          {errorDistributionData.length > 0 && (
            <div className="bg-white rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Error Type Distribution
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={errorDistributionData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="error" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f59e0b" label={{ position: 'right', fill: '#374151', fontSize: 12 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Word-Level Details */}
      {word_details && word_details.length > 0 && (
        <div className="bg-white rounded-lg p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Word-Level Analysis ({filteredWords.length} words)
            </h3>
            <div className="flex gap-2">
              <select
                value={errorFilter}
                onChange={(e) => setErrorFilter(e.target.value)}
                className="px-3 py-1 border rounded-md text-sm"
              >
                <option value="all">All Words</option>
                <option value="errors">Errors Only</option>
                {uniqueErrorTypes.filter(t => t !== 'None').map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'accuracy' | 'word')}
                className="px-3 py-1 border rounded-md text-sm"
              >
                <option value="accuracy">Sort by Accuracy</option>
                <option value="word">Sort Alphabetically</option>
              </select>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Word</th>
                  <th className="text-center p-3 text-sm font-semibold text-gray-700">Accuracy</th>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Error Type</th>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredWords.map((word, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="p-3 font-medium">{word.word}</td>
                    <td className="p-3 text-center">
                      <span className={`
                        inline-block px-3 py-1 rounded-full text-sm font-semibold
                        ${word.accuracy_score >= 90 ? 'bg-green-100 text-green-700' :
                          word.accuracy_score >= 75 ? 'bg-blue-100 text-blue-700' :
                          word.accuracy_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'}
                      `}>
                        {Math.round(word.accuracy_score)}%
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`
                        inline-block px-2 py-1 rounded text-xs font-medium
                        ${word.error_type === 'None' ? 'bg-green-50 text-green-700' :
                          word.error_type === 'Mispronunciation' ? 'bg-red-50 text-red-700' :
                          word.error_type === 'Omission' ? 'bg-orange-50 text-orange-700' :
                          word.error_type === 'Insertion' ? 'bg-purple-50 text-purple-700' :
                          'bg-gray-50 text-gray-700'}
                      `}>
                        {word.error_type}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {word.syllables && word.syllables.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {word.syllables.map((syl, i) => (
                            <span 
                              key={i}
                              className={`
                                px-2 py-0.5 rounded text-xs
                                ${syl.accuracy_score >= 90 ? 'bg-green-100 text-green-700' :
                                  syl.accuracy_score >= 75 ? 'bg-blue-100 text-blue-700' :
                                  'bg-red-100 text-red-700'}
                              `}
                              title={`${syl.syllable}: ${Math.round(syl.accuracy_score)}%`}
                            >
                              {syl.syllable}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Assessment Metadata */}
      <div className="bg-gray-50 rounded-lg p-4 border">
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Assessment ID:</span>
            <span className="ml-2 font-mono text-xs text-gray-800">{assessment.id}</span>
          </div>
          <div>
            <span className="text-gray-600">Created:</span>
            <span className="ml-2 text-gray-800">
              {new Date(assessment.created_at).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Mode:</span>
            <span className="ml-2 text-gray-800 capitalize">
              {assessment.assessment_mode}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
